import { API_BASE_URL } from '../config/api';

import { Artist, CreateArtistRequest, UpdateArtistRequest } from '../types/artist';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export const artistApi = {
  async getAllArtists(page?: number, pageSize?: number): Promise<PagedResult<Artist>> {
    try {
      const params = new URLSearchParams();
      if (page !== undefined) params.append('page', page.toString());
      if (pageSize !== undefined) params.append('pageSize', pageSize.toString());
      
      const url = `${API_BASE_URL}/api/Artist${params.toString() ? '?' + params.toString() : ''}`;
      console.log('Getting all artists');
      console.log('API URL:', url);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get artists response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get artists response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get artists failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get artists');
      }

      return data;
    } catch (error) {
      console.error('Get artists error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getArtistById(id: number): Promise<Artist> {
    try {
      console.log('Getting artist by ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get artist response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get artist response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get artist failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get artist');
      }

      return data;
    } catch (error) {
      console.error('Get artist error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async createArtist(artistData: CreateArtistRequest): Promise<Artist> {
    try {
      console.log('Creating artist with data:', artistData);
      console.log('API URL:', `${API_BASE_URL}/api/Artist`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(artistData),
      });

      console.log('Create artist response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Create artist response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Create artist failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to create artist');
      }

      return data;
    } catch (error) {
      console.error('Create artist error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async updateArtist(id: number, artistData: UpdateArtistRequest): Promise<void> {
    try {
      console.log('Updating artist with data:', artistData);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}`, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(artistData),
      });

      console.log('Update artist response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to update artist';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Update artist failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Update artist error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async deleteArtist(id: number): Promise<void> {
    try {
      console.log('Deleting artist with ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete artist response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to delete artist';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Delete artist failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Delete artist error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getArtistsByUserId(userId: string): Promise<Artist[]> {
    try {
      console.log('Getting artists by user ID:', userId);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/user/${userId}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/user/${userId}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get artists by user response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get artists by user response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get artists by user failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get artists');
      }

      return data;
    } catch (error) {
      console.error('Get artists by user error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getProfileImage(id: number): Promise<Blob> {
    try {
      console.log('Getting profile image for artist ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}/profile-image`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}/profile-image`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Get profile image response status:', response.status);

      if (!response.ok) {
        console.error('Get profile image failed with status:', response.status);
        throw new ApiError(response.status, 'Failed to get profile image');
      }

      return await response.blob();
    } catch (error) {
      console.error('Get profile image error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async uploadProfileImage(id: number, imageFile: File): Promise<Artist> {
    try {
      console.log('Uploading profile image for artist ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}/profile-image`);
      
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('imageFile', imageFile);

      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}/profile-image`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log('Upload profile image response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Upload profile image response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Upload profile image failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to upload profile image');
      }

      return data;
    } catch (error) {
      console.error('Upload profile image error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async deleteProfileImage(id: number): Promise<void> {
    try {
      console.log('Deleting profile image for artist ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}/profile-image`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}/profile-image`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete profile image response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to delete profile image';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Delete profile image failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Delete profile image error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async blockArtist(id: number): Promise<void> {
    try {
      console.log('Blocking artist with ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}/block`);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}/block`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Block artist response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to block artist';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Block artist failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Block artist error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async unblockArtist(id: number): Promise<void> {
    try {
      console.log('Unblocking artist with ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}/block`);

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}/block`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Unblock artist response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to unblock artist';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Unblock artist failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Unblock artist error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async refreshEntitlements(id: number): Promise<void> {
    try {
      console.log('Refreshing entitlements for artist ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Artist/${id}/entitlements/refresh`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Artist/${id}/entitlements/refresh`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Refresh entitlements response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to refresh entitlements';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Refresh entitlements failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Refresh entitlements error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },
};
